import { useState, useRef, useEffect, type ComponentType } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Users,
  BarChart3,
  Plus,
  Edit3,
  Search,
  ShieldCheck,
  Crown,
  CircleDot,
  Download,
  FileText,
  Loader2,
  UserX,
  Trash2,
  Camera,
  User as UserIcon,
  Eye,
  EyeOff,
  Building2,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  type LucideProps,
} from "lucide-react";
import {
  api,
  apiError,
  type Paginated,
  type User,
  type Role,
} from "../lib/api";
import { GlassCard } from "../components/ui/GlassCard";
import { Modal } from "../components/ui/Modal";
import { Avatar } from "../components/ui/Avatar";
import { Spinner } from "../components/ui/FullScreenLoader";
import { fmtRelative } from "../lib/format";
import { ROLES } from "../lib/constants";
import { cn } from "../lib/cn";
import { useReactToPrint } from "react-to-print";
import { Label } from "../components/ui/label";
import { Switch } from "../components/animate-ui/components/headless/switch";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPopup,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/animate-ui/components/base/alert-dialog";
import {
  useAgentDepartments,
  useItDepartments,
  addDepartment,
  removeDepartment,
  renameDepartment,
  reorderDepartment,
  resetDepartments,
  type DeptGroup,
} from "../lib/departments";

type TabId = "users" | "departments" | "reports";
const TABS: { id: TabId; label: string; icon: ComponentType<LucideProps> }[] =
  [
    { id: "users", label: "Users", icon: Users },
    { id: "departments", label: "Departments", icon: Building2 },
    { id: "reports", label: "Reports", icon: BarChart3 },
  ];

export default function Management() {
  const [tab, setTab] = useState<TabId>("users");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="page-title">Management</h1>
        <p className="page-subtitle">
          Manage users, departments, and view team performance reports.
        </p>
      </div>

      <div className="glass-subtle rounded-2xl p-1 inline-flex">
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={cn(
                "px-3 py-1.5 rounded-xl text-sm flex items-center gap-2 transition-all",
                active
                  ? "bg-white/10 text-white shadow-glass-sm"
                  : "text-slate-300 hover:bg-white/5",
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "departments" && <DepartmentsTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}

// ===================== Users =====================

function formatPhoneNumber(val: string) {
  if (!val) return "";
  const digits = val.replace(/\D/g, "");
  let core = digits;
  if (core.startsWith("1")) {
    core = core.slice(1);
  }
  core = core.slice(0, 10);
  if (core.length === 0) return "";
  if (core.length <= 3) return `1(${core}`;
  if (core.length <= 6) return `1(${core.slice(0, 3)})-${core.slice(3)}`;
  return `1(${core.slice(0, 3)})-${core.slice(3, 6)}-${core.slice(6)}`;
}

const createSchema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().email(),
  phone_number: z.string().optional().nullable(),
  password: z.string().min(8).max(72),
  role: z.enum(["AGENT", "IT", "ADMIN"]),
  department: z.string().trim().max(100).optional(),
  additionalDepartments: z.array(z.string()).optional().nullable(),
});
type CreateForm = z.infer<typeof createSchema>;

const editSchema = z.object({
  name: z.string().trim().min(2).max(100),
  phone_number: z.string().optional().nullable(),
  role: z.enum(["AGENT", "IT", "ADMIN"]),
  department: z.string().trim().max(100).optional().nullable(),
  additionalDepartments: z.array(z.string()).optional().nullable(),
  isActive: z.boolean(),
  password: z.string().min(8).max(72).optional().or(z.literal("")),
});
type EditForm = z.infer<typeof editSchema>;

function UsersTab() {
  const qc = useQueryClient();
  const agentDepartments = useAgentDepartments();
  const itDepartments = useItDepartments();
  const allDepartments = [...agentDepartments, ...itDepartments];
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin.users", q, department, sortBy],
    queryFn: async () =>
      (
        await api.get<Paginated<User>>("/api/users", {
          params: { q, department, sortBy, pageSize: 100 },
        })
      ).data,
  });

  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/users/${encodeURIComponent(id)}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin.users"] });
      toast.success("User deactivated");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const permanentDel = useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/api/users/${encodeURIComponent(id)}/permanent`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin.users"] });
      toast.success("User permanently deleted");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      <GlassCard className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            className="select w-auto"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          >
            <option value="">All Departments</option>
            {allDepartments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            className="select w-auto"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">Sort by Newest</option>
            <option value="department">Sort by Department</option>
          </select>
          <button
            onClick={() => setCreating(true)}
            className="btn-primary flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> New User
          </button>
          <button
            onClick={() => setExporting(true)}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </GlassCard>

      <GlassCard padded={false}>
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton h-12" />
            ))}
          </div>
        ) : !data?.items?.length ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No users found.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {/* Table Header */}
            <div className="hidden lg:grid grid-cols-12 gap-3 items-center px-4 py-3 bg-white/10 border-b border-white/10 text-[10px] font-bold uppercase tracking-widest text-slate-300 rounded-t-2xl">
              <div className="col-span-3 pl-11">User</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">Department</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Last Login</div>
              <div className="col-span-2 pl-30">Actions</div>
            </div>
            {data.items.map((u) => (
              <div
                key={u.id}
                className="grid grid-cols-12 gap-3 items-center px-4 py-3"
              >
                <div className="col-span-12 lg:col-span-3 flex items-center gap-3 min-w-0">
                  <Avatar name={u.name} url={u.avatarUrl} size="sm" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{u.name}</div>
                    <div className="text-xs text-slate-400 truncate">{u.email}</div>
                  </div>
                </div>
                <div className="col-span-6 lg:col-span-2">
                  <RoleBadge role={u.role} />
                </div>
                <div className="col-span-6 lg:col-span-2 text-xs text-slate-300 truncate">
                  {u.department || <span className="italic text-slate-500">—</span>}
                </div>
                <div className="col-span-6 lg:col-span-2">
                  {u.isActive ? (
                    <span className="badge text-emerald-200 bg-emerald-500/15 border-emerald-400/40">
                      <CircleDot className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="badge text-slate-300 bg-white/5 border-white/10">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="col-span-6 lg:col-span-1 text-[11px] text-slate-400">
                  {fmtRelative(u.lastLoginAt)}
                </div>
                <div className="col-span-12 lg:col-span-2 flex items-center justify-end gap-2">
                  <button
                    className="group flex items-center justify-start h-8 max-w-[32px] hover:max-w-[140px] px-2 overflow-hidden whitespace-nowrap rounded-xl transition-all duration-300 ease-out bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300 hover:text-white"
                    onClick={() => setEditing(u)}
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4 shrink-0" />
                    <span className="ml-2 text-xs font-medium opacity-0 translate-x-2 invisible group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 delay-75">
                      Edit
                    </span>
                  </button>
                  {u.isActive && (
                    <AlertDialog>
                      <AlertDialogTrigger 
                        className="group flex items-center justify-start h-8 max-w-[32px] hover:max-w-[140px] px-2 overflow-hidden whitespace-nowrap rounded-xl transition-all duration-300 ease-out bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300 hover:text-critical-200" 
                        title="Deactivate"
                      >
                        <UserX className="w-4 h-4 shrink-0" />
                        <span className="ml-2 text-xs font-medium opacity-0 translate-x-2 invisible group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 delay-75">
                          Deactivate
                        </span>
                      </AlertDialogTrigger>
                      <AlertDialogPopup>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Deactivate {u.name}?</AlertDialogTitle>
                          <AlertDialogDescription>They will no longer be able to log in to the system.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del.mutate(u.id)} className="btn-critical">Deactivate</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogPopup>
                    </AlertDialog>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger 
                      className="group flex items-center justify-start h-8 max-w-[32px] hover:max-w-[140px] px-2 overflow-hidden whitespace-nowrap rounded-xl transition-all duration-300 ease-out bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300 hover:text-red-400" 
                      title="Delete permanently"
                    >
                      <Trash2 className="w-4 h-4 shrink-0" />
                      <span className="ml-2 text-xs font-medium opacity-0 translate-x-2 invisible group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 delay-75">
                        Delete
                      </span>
                    </AlertDialogTrigger>
                    <AlertDialogPopup>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>Permanently delete this user? This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => permanentDel.mutate(u.id)} className="btn-critical">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogPopup>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      <CreateUserModal open={creating} onClose={() => setCreating(false)} />
      <EditUserModal user={editing} onClose={() => setEditing(null)} />
      <ExportModal open={exporting} onClose={() => setExporting(false)} q={q} />
    </>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const styles =
    {
      ADMIN: { icon: Crown, cls: "text-amber-200 bg-amber-500/15 border-amber-400/40" },
      IT: { icon: ShieldCheck, cls: "text-brand-200 bg-brand-500/15 border-brand-400/40" },
      AGENT: { icon: Users, cls: "text-slate-300 bg-white/5 border-white/10" },
    }[role] || { icon: Users, cls: "text-slate-300 bg-white/5 border-white/10" };
  const Icon = styles.icon;
  return (
    <span className={`badge ${styles.cls}`}>
      <Icon className="w-3 h-3" /> {role}
    </span>
  );
}

// Department lists now come from the shared reactive store (useAgentDepartments / useItDepartments).

function CreateUserModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { role: "AGENT" } as CreateForm,
  });

  const selectedRole = watch("role");
  const agentDepts = useAgentDepartments();
  const itDepts = useItDepartments();
  const allDepts = [...agentDepts, ...itDepts];
  const departmentOptions = selectedRole === "ADMIN" ? allDepts : (selectedRole === "IT" ? itDepts : agentDepts);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const create = useMutation({
    mutationFn: async (data: CreateForm) => {
      const res = (await api.post("/api/users", data)).data;
      if (avatarFile) {
        const fd = new FormData();
        fd.append("avatar", avatarFile);
        await api.post(`/api/users/${encodeURIComponent(res.user.id)}/avatar`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin.users"] });
      toast.success("User created");
      reset();
      setAvatarFile(null);
      setPreviewUrl(null);
      onClose();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Modal open={open} onClose={onClose} title="Create user">
      <form
        onSubmit={handleSubmit((d) => create.mutate(d))}
        className="space-y-3"
      >
        <div className="flex flex-col items-center justify-center pt-2 pb-4 relative">
          <label htmlFor="avatar-upload-create" className="w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden mb-2 relative group cursor-pointer shadow-glass-sm hover:border-brand-500/50 transition-colors">
            {previewUrl ? (
              <img src={previewUrl} alt="Profile preview" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-10 h-10 text-slate-400 group-hover:text-brand-300 transition-colors" />
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[2px]">
               <Camera className="w-6 h-6 text-white" />
            </div>
          </label>
          <input
            id="avatar-upload-create"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setAvatarFile(file);
                setPreviewUrl(URL.createObjectURL(file));
              }
            }}
          />
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Profile Picture</span>
        </div>

        <div>
          <label className="label">Name</label>
          <input className="input" {...register("name")} />
          {errors.name && <p className="field-error">{errors.name.message}</p>}
        </div>
        <div>
          <label className="label">Email</label>
          <input type="email" className="input" {...register("email")} />
          {errors.email && <p className="field-error">{errors.email.message}</p>}
        </div>
        <div>
          <label className="label">Phone Number</label>
          <input
            className="input"
            placeholder="1(000)-000-0000"
            {...register("phone_number", {
              onChange: (e) => {
                e.target.value = formatPhoneNumber(e.target.value);
              },
            })}
          />
        </div>
        <div>
          <label className="label">Password</label>
          <div className="relative">
            <input 
              type={showPassword ? "text" : "password"} 
              className="input pr-10" 
              {...register("password")} 
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="field-error">{errors.password.message}</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Role</label>
            <select
              className="select"
              {...register("role", {
                onChange: () => {
                  setValue("department", "");
                },
              })}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Department</label>
            <select className="select" {...register("department")}>
              <option value="">No Department</option>
              {departmentOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(selectedRole === "IT" || selectedRole === "ADMIN") && (
          <div>
            <label className="label">Additional Departments</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {itDepts.map((d) => (
                <label key={d} className="inline-flex items-center gap-1.5 text-sm text-slate-300 bg-white/5 border border-white/10 px-2 py-1 rounded-md cursor-pointer hover:bg-white/10 transition-colors">
                  <input
                    type="checkbox"
                    value={d}
                    {...register("additionalDepartments")}
                    disabled={watch("department") === d}
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="pt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? <Spinner /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
}: {
  user: User | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(user?.avatarUrl || null);
  const [showPassword, setShowPassword] = useState(false);
  const open = !!user;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: user
      ? {
          name: user.name,
          phone_number: user.phone_number || "",
          role: user.role,
          department: user.department || "",
          additionalDepartments: user.additionalDepartments || [],
          isActive: user.isActive ?? true,
          password: "",
        }
      : undefined,
  });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name,
        phone_number: user.phone_number || "",
        role: user.role,
        department: user.department || "",
        additionalDepartments: user.additionalDepartments || [],
        isActive: user.isActive ?? true,
        password: "",
      });
      setPreviewUrl(user.avatarUrl || null);
      setShowPassword(false);
    }
  }, [user, reset]);

  const selectedRole = watch("role");
  const agentDepts = useAgentDepartments();
  const itDepts = useItDepartments();
  const allDepts = [...agentDepts, ...itDepts];
  const departmentOptions = selectedRole === "ADMIN" ? allDepts : (selectedRole === "IT" ? itDepts : agentDepts);

  const save = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: EditForm & { id: string }) => {
      const payload: Record<string, unknown> = { ...data };
      if (!payload.password) delete payload.password;
      const res = (await api.patch(`/api/users/${encodeURIComponent(id)}`, payload)).data;
      if (avatarFile) {
        const fd = new FormData();
        fd.append("avatar", avatarFile);
        await api.post(`/api/users/${encodeURIComponent(id)}/avatar`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin.users"] });
      toast.success("User updated");
      setAvatarFile(null);
      onClose();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!user) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Edit · ${user.name}`}>
      <form
        onSubmit={handleSubmit((d) => save.mutate({ ...d, id: user.id }))}
        className="space-y-3"
      >
        <div className="flex flex-col items-center justify-center pt-2 pb-4 relative">
          <label htmlFor="avatar-upload-edit" className="w-24 h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden mb-2 relative group cursor-pointer shadow-glass-sm hover:border-brand-500/50 transition-colors">
            {previewUrl ? (
              <img src={previewUrl} alt="Profile preview" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-10 h-10 text-slate-400 group-hover:text-brand-300 transition-colors" />
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[2px]">
               <Camera className="w-6 h-6 text-white" />
            </div>
          </label>
          <input
            id="avatar-upload-edit"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setAvatarFile(file);
                setPreviewUrl(URL.createObjectURL(file));
              }
            }}
          />
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Profile Picture</span>
        </div>

        <div>
          <label className="label">Name</label>
          <input className="input" {...register("name")} />
          {errors.name && <p className="field-error">{errors.name.message}</p>}
        </div>
        <div>
          <label className="label">Phone Number</label>
          <input
            className="input"
            placeholder="1(000)-000-0000"
            {...register("phone_number", {
              onChange: (e) => {
                e.target.value = formatPhoneNumber(e.target.value);
              },
            })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Role</label>
            <select
              className="select"
              {...register("role", {
                onChange: () => {
                  setValue("department", "");
                },
              })}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Department</label>
            <select className="select" {...register("department")}>
              <option value="">No Department</option>
              {departmentOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(selectedRole === "IT" || selectedRole === "ADMIN") && (
          <div>
            <label className="label">Additional Departments</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {itDepts.map((d) => (
                <label key={d} className="inline-flex items-center gap-1.5 text-sm text-slate-300 bg-white/5 border border-white/10 px-2 py-1 rounded-md cursor-pointer hover:bg-white/10 transition-colors">
                  <input
                    type="checkbox"
                    value={d}
                    {...register("additionalDepartments")}
                    disabled={watch("department") === d}
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="label">New password (optional)</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className="input pr-10"
              placeholder="Leave blank to keep current"
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="field-error">{errors.password.message}</p>
          )}
        </div>
        <Label className="flex items-center gap-x-3 text-sm text-slate-200 cursor-pointer">
          <Switch {...register("isActive")} />
          Active
        </Label>
        <div className="pt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : <Edit3 className="w-4 h-4" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ===================== Import & Export Utilities =====================

function downloadCSV(users: User[]) {
  const headers = ["Name", "Email", "Role", "Department", "Is Active"];
  const rows = users.map((u) => [
    `"${u.name.replace(/"/g, '""')}"`,
    `"${u.email.replace(/"/g, '""')}"`,
    `"${u.role}"`,
    `"${(u.department || "").replace(/"/g, '""')}"`,
    u.isActive ? "Active" : "Inactive",
  ]);
  const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join(
    "\n",
  );
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `impact_users_${new Date().toISOString().split("T")[0]}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadJSON(users: User[]) {
  const content = JSON.stringify(
    users.map((u) => ({
      name: u.name,
      email: u.email,
      role: u.role,
      department: u.department,
      isActive: u.isActive,
    })),
    null,
    2,
  );
  const blob = new Blob([content], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `impact_users_${new Date().toISOString().split("T")[0]}.json`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function ExportModal({
  open,
  onClose,
  q,
}: {
  open: boolean;
  onClose: () => void;
  q: string;
}) {
  const [format, setFormat] = useState<"csv" | "json">("csv");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let usersToExport: User[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const res = await api.get<Paginated<User>>("/api/users", {
          params: { q, page, pageSize: 100 },
        });
        const items = res.data?.items || [];
        usersToExport = [...usersToExport, ...items];

        const totalPages = res.data?.totalPages || 1;
        if (page >= totalPages || items.length < 100) {
          hasMore = false;
        } else {
          page++;
        }
      }

      if (!usersToExport.length) {
        toast.error("No users found to export");
        onClose();
        return;
      }

      if (format === "csv") {
        downloadCSV(usersToExport);
      } else {
        downloadJSON(usersToExport);
      }
      toast.success(`Successfully exported ${usersToExport.length} users`);
      onClose();
    } catch (e) {
      toast.error("Failed to export users: " + apiError(e));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Export Users">
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          Export user accounts. The export will include names, emails, roles,
          departments, and active statuses.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setFormat("csv")}
            className={cn(
              "p-4 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all",
              format === "csv"
                ? "bg-white/10 border-brand-500/40 text-brand-200"
                : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
            )}
          >
            <FileText className="w-8 h-8 text-brand-400" />
            <span className="font-semibold text-sm">CSV Format</span>
            <span className="text-[10px] text-slate-400">
              Great for Excel & Sheets
            </span>
          </button>

          <button
            onClick={() => setFormat("json")}
            className={cn(
              "p-4 rounded-2xl border flex flex-col items-center justify-center gap-2 transition-all",
              format === "json"
                ? "bg-white/10 border-indigo-500/40 text-indigo-200"
                : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
            )}
          >
            <FileText className="w-8 h-8 text-indigo-400" />
            <span className="font-semibold text-sm">JSON Format</span>
            <span className="text-[10px] text-slate-400">
              Great for system backups
            </span>
          </button>
        </div>

        <div className="pt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="btn-primary flex items-center gap-1.5"
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ===================== Departments =====================

function DepartmentsTab() {
  return (
    <div className="space-y-4">
      <DepartmentGroup group="agent" title="Agent Departments" accent="slate" />
      <DepartmentGroup group="it" title="IT Departments" accent="brand" />
    </div>
  );
}

function DepartmentGroup({
  group,
  title,
  accent,
}: {
  group: DeptGroup;
  title: string;
  accent: "slate" | "brand";
}) {
  const agentDepts = useAgentDepartments();
  const itDepts = useItDepartments();
  const departments = group === "agent" ? agentDepts : itDepts;
  const [newName, setNewName] = useState("");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const ok = addDepartment(group, trimmed);
    if (ok) {
      toast.success(`Added "${trimmed}"`);
      setNewName("");
    } else {
      toast.error("Department already exists");
    }
  };

  const handleRename = (oldName: string) => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === oldName) {
      setEditingIdx(null);
      return;
    }
    const ok = renameDepartment(group, oldName, trimmed);
    if (ok) {
      toast.success(`Renamed "${oldName}" → "${trimmed}"`);
      setEditingIdx(null);
    } else {
      toast.error("A department with that name already exists");
    }
  };

  const handleRemove = (name: string) => {
    removeDepartment(group, name);
    toast.success(`Removed "${name}"`);
  };

  const handleReset = () => {
    resetDepartments(group);
    toast.success(`${title} reset to defaults`);
  };

  const accentClasses =
    accent === "brand"
      ? {
          header: "bg-brand-500/10 border-brand-400/20",
          badge: "text-brand-200 bg-brand-500/15 border-brand-400/40",
          addBtn: "btn-primary",
        }
      : {
          header: "bg-white/[0.04] border-white/10",
          badge: "text-slate-200 bg-white/5 border-white/10",
          addBtn: "btn-primary",
        };

  return (
    <GlassCard padded={false}>
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between px-5 py-3 border-b rounded-t-2xl",
          accentClasses.header,
        )}
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-300" />
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <span
            className={cn(
              "badge text-[10px] px-1.5 py-0.5",
              accentClasses.badge,
            )}
          >
            {departments.length}
          </span>
        </div>

        <AlertDialog>
          <AlertDialogTrigger className="group flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors">
            <RotateCcw className="w-3 h-3" />
            Reset
          </AlertDialogTrigger>
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset {title}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will replace the current list with the original defaults.
                Any custom departments will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReset}
                className="btn-critical"
              >
                Reset to Defaults
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </div>

      {/* Add new */}
      <div className="px-5 py-3 border-b border-white/5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAdd();
          }}
          className="flex items-center gap-2"
        >
          <div className="relative flex-1">
            <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Add new department…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="input pl-10"
            />
          </div>
          <button
            type="submit"
            disabled={!newName.trim()}
            className={cn(accentClasses.addBtn, "flex items-center gap-1.5")}
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </form>
      </div>

      {/* List */}
      {departments.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">
          No departments configured. Add one above or reset to defaults.
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {departments.map((dept, idx) => (
            <div
              key={`${dept}-${idx}`}
              className="flex items-center gap-3 px-5 py-2.5 group/row hover:bg-white/[0.02] transition-colors"
            >
              {/* Order controls */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => reorderDepartment(group, dept, "up")}
                  disabled={idx === 0}
                  className="p-0.5 rounded text-slate-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  title="Move up"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => reorderDepartment(group, dept, "down")}
                  disabled={idx === departments.length - 1}
                  className="p-0.5 rounded text-slate-500 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                  title="Move down"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>

              {/* Name or edit input */}
              <div className="flex-1 min-w-0">
                {editingIdx === idx ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleRename(dept);
                    }}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="input text-sm py-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setEditingIdx(null);
                      }}
                    />
                    <button
                      type="submit"
                      className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                      title="Save"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingIdx(null)}
                      className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
                      title="Cancel"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </form>
                ) : (
                  <span className="text-sm text-slate-200 truncate block">
                    {dept}
                  </span>
                )}
              </div>

              {/* Actions (hidden until hover, except on mobile) */}
              {editingIdx !== idx && (
                <div className="flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                  <button
                    onClick={() => {
                      setEditingIdx(idx);
                      setEditValue(dept);
                    }}
                    className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
                    title="Rename"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>

                  <AlertDialog>
                    <AlertDialogTrigger
                      className="p-1.5 rounded-lg bg-white/5 text-slate-400 hover:bg-red-500/15 hover:text-red-300 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </AlertDialogTrigger>
                    <AlertDialogPopup>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Remove "{dept}"?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          This department will be removed from the dropdown
                          options. Users already assigned to this department
                          will keep their current assignment.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleRemove(dept)}
                          className="btn-critical"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogPopup>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

// ===================== Reports =====================

function ReportsTab() {
  const [periodType, setPeriodType] = useState<"30d" | "month" | "week" | "day">("30d");
  const [periodValue, setPeriodValue] = useState("");

  const queryParam = periodType === "30d" ? "" : periodValue;
  const contentRef = useRef<HTMLDivElement>(null);

  const summary = useQuery<any>({
    queryKey: ["admin.summary", queryParam],
    queryFn: async () => (await api.get("/api/reports/summary", { params: { month: queryParam || undefined } })).data,
  });
  const sla = useQuery<any>({
    queryKey: ["admin.sla", queryParam],
    queryFn: async () => (await api.get("/api/reports/sla", { params: { month: queryParam || undefined } })).data,
  });
  const agents = useQuery<any[]>({
    queryKey: ["admin.agents", queryParam],
    queryFn: async () => (await api.get("/api/reports/agents", { params: { month: queryParam || undefined } })).data.items,
  });

  const handleExportPdf = useReactToPrint({
    contentRef,
    documentTitle: `report-${queryParam || "last-30-days"}`,
  });

  const getLabel = () => {
    if (periodType === "30d" || !periodValue) return "30d";
    if (periodType === "month") return periodValue;
    if (periodType === "week" && periodValue.includes("-W")) {
      const [y, w] = periodValue.split("-W");
      return `Week ${w}, ${y}`;
    }
    return periodValue;
  };
  const label = getLabel();

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="select w-auto"
            value={periodType}
            onChange={(e) => {
              setPeriodType(e.target.value as any);
              setPeriodValue("");
            }}
          >
            <option value="30d">Last 30 Days</option>
            <option value="month">Specific Month</option>
            <option value="week">Specific Week</option>
            <option value="day">Specific Day</option>
          </select>

          {periodType === "month" && (
            <input
              type="month"
              value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
              className="input w-auto"
            />
          )}
          {periodType === "week" && (
            <input
              type="week"
              value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
              className="input w-auto"
            />
          )}
          {periodType === "day" && (
            <input
              type="date"
              value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
              className="input w-auto"
            />
          )}

          {periodType !== "30d" && periodValue && (
            <button
              className="text-xs text-slate-400 hover:text-white transition-colors"
              onClick={() => {
                setPeriodType("30d");
                setPeriodValue("");
              }}
            >
              Clear filter
            </button>
          )}
        </div>
        <button
          onClick={() => handleExportPdf()}
          className="btn-secondary flex items-center gap-1.5"
        >
          <Download className="w-4 h-4" />
          Export PDF
        </button>
      </div>

      <div id="report-content" ref={contentRef} className="space-y-4 p-2 -m-2 print:p-8 print:m-0">
        {/* SLA metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 print:grid-cols-4 gap-3">
          <Metric label={`Resolved (${label})`} value={sla.data?.resolvedCount ?? "—"} />
          <Metric
            label="On-time rate"
            value={
              sla.data?.onTimeRate !== null && sla.data?.onTimeRate !== undefined
                ? `${(sla.data.onTimeRate * 100).toFixed(0)}%`
                : "—"
            }
            accent="emerald"
          />
          <Metric
            label="Avg resolution"
            value={
              sla.data?.avgResolutionHours !== null &&
                sla.data?.avgResolutionHours !== undefined
                ? `${sla.data.avgResolutionHours} h`
                : "—"
            }
          />
          <Metric
            label="Open & breached"
            value={sla.data?.openBreached ?? "—"}
            accent="critical"
          />
        </div>

        {/* Agent leaderboard */}
        <GlassCard padded={false}>
          <div className="p-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">
              Agent Performance {periodType === "30d" || !periodValue ? "(last 30 days)" : `(${label})`}
            </h3>
            <span className="text-[11px] uppercase tracking-wider text-slate-400">
              {summary.data?.totals?.activeUsers ?? "—"} active users
            </span>
          </div>
          {agents.isLoading ? (
            <div className="p-4 space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="skeleton h-12" />
              ))}
            </div>
          ) : !agents.data?.length ? (
            <div className="p-8 text-center text-sm text-slate-400">
              No agents yet.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {agents.data.map((a) => (
                <div
                  key={a.id}
                  className="grid grid-cols-12 gap-3 items-center px-4 py-3 print:break-inside-avoid"
                >
                  <div className="col-span-12 lg:col-span-4 print:col-span-4 flex items-center gap-3 min-w-0">
                    <Avatar name={a.name} url={a.avatarUrl} size="sm" />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{a.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">
                        {a.role}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6 lg:col-span-2 print:col-span-2 text-sm">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      Open
                    </div>
                    <div className="text-white font-semibold">{a.openCount}</div>
                  </div>
                  <div className="col-span-6 lg:col-span-2 print:col-span-2 text-sm">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      Resolved
                    </div>
                    <div className="text-emerald-200 font-semibold">
                      {a.resolvedCount}
                    </div>
                  </div>
                  <div className="col-span-6 lg:col-span-2 print:col-span-2 text-sm">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      Avg hours
                    </div>
                    <div className="text-white font-semibold">
                      {a.avgResolutionHours ?? "—"}
                    </div>
                  </div>
                  <div className="col-span-6 lg:col-span-2 print:col-span-2 text-sm">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      Breached
                    </div>
                    <div className="text-critical-200 font-semibold">
                      {a.breachedCount}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  accent = "brand",
}: {
  label: string;
  value: React.ReactNode;
  accent?: "brand" | "emerald" | "critical";
}) {
  const accents = {
    brand: "text-brand-200",
    emerald: "text-emerald-200",
    critical: "text-critical-200",
  }[accent];
  return (
    <GlassCard>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${accents}`}>{value}</div>
    </GlassCard>
  );
}
